// Speak (dictation, everywhere there is a mic button): record in the browser (60 seconds at most), measure the live
// input level for the waveform and the orb, then send the audio to be transcribed. The audio is discarded after
// transcription (server rule). About two seconds of quiet after speech stops the recording by itself, and stop({ send:
// true }) (Send pressed while recording) stops, transcribes and hands the words on to be sent in one go. A recording
// with no real words in it comes back as "Didn't catch that" and nothing is sent.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionValue, type MotionValue } from 'motion/react';
import { api } from '../api';
import { resumeAudioContext } from '../voice/audio';

export const MAX_SECONDS = 60;
/** Quiet after speech that ends a dictation by itself. */
export const SILENCE_STOP_MS = 2000;
/** Speech starts above START (for a few frames) and has stopped below STOP, on the eased 0..1 level. */
export const LEVEL_START = 0.16, LEVEL_STOP = 0.07;
export const canRecord = () => typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
export const recordingType = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find(t => window.MediaRecorder.isTypeSupported?.(t)) ?? '';
/** Words written down from speech, after what is already typed. */
export const joinWords = (typed: string, said: string) => (typed.trim() ? `${typed.trim()} ${said}` : said);
/** The file name a recording is uploaded under, by its type (Safari records mp4). */
export const fileFor = (blob: Blob, base: string) => `${base}.${blob.type.includes('mp4') ? 'm4a' : 'webm'}`;

export function micError(e: unknown) {
  const name = e instanceof DOMException ? e.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Microphone access is blocked. Allow it for this site in the browser settings, or type instead.';
  if (name === 'NotFoundError') return 'No microphone was found. Type instead.';
  return e instanceof Error ? e.message : 'Could not record.';
}

/**
 * The microphone's level, every frame: RMS of the waveform mapped to 0..1 on a soft curve and eased (speech sits
 * around 0.02 to 0.2 RMS). Uses the shared AudioContext resumed in the tap (resumeAudioContext), else its own.
 * Returns a stop function. live: false when the context is not running (then nothing can be measured, and callers must
 * not wait for a silence they cannot hear).
 */
export function meterLevel(stream: MediaStream, onFrame: (level: number, live: boolean) => void): () => void {
  let ac: AudioContext | null = resumeAudioContext();
  let own = false;
  if (!ac) { try { ac = new AudioContext(); own = true; } catch { ac = null; } }
  if (!ac) { const t = window.setInterval(() => onFrame(0, false), 100); return () => window.clearInterval(t); }
  const src = ac.createMediaStreamSource(stream);
  const an = ac.createAnalyser();
  an.fftSize = 512;
  src.connect(an);
  const buf = new Float32Array(an.fftSize);
  let eased = 0, raf = 0;
  const tick = () => {
    an.getFloatTimeDomainData(buf);
    let sum = 0;
    for (const v of buf) sum += v * v;
    eased += (Math.min(1, Math.pow(Math.sqrt(sum / buf.length) * 6, 0.7)) - eased) * 0.35;
    onFrame(eased, ac!.state === 'running');
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(raf);
    try { src.disconnect(); } catch { /* already */ }
    if (own) void ac!.close().catch(() => undefined);
  };
}

export type RecState = 'idle' | 'asking' | 'recording' | 'working';
/** send: Send was pressed while recording, so the words go straight on. */
export type OnText = (text: string, demo: boolean, o: { send: boolean }) => void;

/**
 * level: the input loudness, 0 to 1, updated every frame while recording. bars: the last few levels, newest last, for
 * a scrolling waveform. onText gets the transcript (never empty). start() must be called from the tap itself.
 */
export function useRecorder(onText: OnText) {
  const [state, setState] = useState<RecState>('idle');
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const level = useMotionValue(0);
  const bars = useRef<number[]>(Array(28).fill(0));
  const rec = useRef<MediaRecorder | null>(null);
  const meter = useRef<(() => void) | null>(null);
  const sendAfter = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const cb = useRef(onText);
  cb.current = onText;

  const cleanup = useCallback(() => {
    meter.current?.();
    meter.current = null;
    window.clearInterval(timer.current);
    rec.current?.stream.getTracks().forEach(t => t.stop());
    level.set(0);
  }, [level]);
  useEffect(() => cleanup, [cleanup]);

  const start = useCallback(async () => {
    setError(''); setState('asking');
    sendAfter.current = false;
    // In the tap, before anything is awaited: the level meter's AudioContext must be running (iOS).
    resumeAudioContext();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = recordingType();
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks: Blob[] = [];
      r.ondataavailable = e => { if (e.data.size) chunks.push(e.data); };
      r.onstop = async () => {
        cleanup();
        const send = sendAfter.current;
        sendAfter.current = false;
        const blob = new Blob(chunks, { type: r.mimeType || type || 'audio/webm' });
        if (!blob.size) { setState('idle'); return; }
        setState('working');
        try {
          const t = await api.transcribe(blob, fileFor(blob, 'recording'));
          cb.current(t.text, t.demo, { send });
        } catch (e) { setError(e instanceof Error ? e.message : 'Could not write that down.'); } finally { setState('idle'); }
      };
      rec.current = r;
      // Speech, then about two seconds of quiet: stop by itself.
      let loud = 0, spoke = false, quietSince = 0, frame = 0;
      meter.current = meterLevel(stream, (v, live) => {
        level.set(v);
        if (++frame % 3 === 0) { bars.current.push(v); bars.current.shift(); }
        if (!live || r.state !== 'recording') return;
        const now = performance.now();
        if (!spoke) { loud = v > LEVEL_START ? loud + 1 : 0; if (loud >= 3) spoke = true; return; }
        if (v < LEVEL_STOP) { if (!quietSince) quietSince = now; else if (now - quietSince > SILENCE_STOP_MS) r.stop(); } else quietSince = 0;
      });
      r.start();
      setSeconds(0);
      setState('recording');
      let n = 0;
      timer.current = window.setInterval(() => {
        n += 1;
        setSeconds(n);
        if (n >= MAX_SECONDS && r.state === 'recording') r.stop();
      }, 1000);
    } catch (e) { cleanup(); setState('idle'); setError(micError(e)); }
  }, [cleanup, level]);

  /** Stop now. send: Send was pressed while recording, so the words are sent as soon as they are written down. */
  const stop = useCallback((o: { send?: boolean } = {}) => {
    if (rec.current?.state !== 'recording') return;
    sendAfter.current = !!o.send;
    rec.current.stop();
  }, []);
  return { state, error, seconds, level: level as MotionValue<number>, bars, start, stop };
}
